import {
  AbsoluteCenter,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { canDeleteDealApi, deleteDealApi, getDealsApi } from 'api/dealApi';
import DataTable from 'components/DataTable';
import Card from 'components/card/Card';
import { CockpitUsersContext } from 'context/CockpitUsersContext';
import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const DealsOverview = (props) => {
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [dealToDelete, setDealToDelete] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { getUserName } = useContext(CockpitUsersContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: 'Deal Name',
    }),
    columnHelper.accessor('createdAtMillis', {
      cell: (info) => {
        return moment(info.getValue()).format('MMM DD, YYYY');
      },
      header: 'Created at',
    }),
    columnHelper.accessor('userId', {
      cell: (info) => {
        const userId = info.getValue();
        return getUserName(info.getValue()) || userId;
      },
      header: 'Created by',
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => (
        <IconButton
          icon={<DeleteIcon />}
          size="sm"
          colorScheme="red"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(info.row.original);
          }}
          aria-label="Delete deal"
        />
      ),
    }),
  ];

  const handleDeleteClick = async (deal) => {
    try {
      const { canDelete, reason } = await canDeleteDealApi({
        customerId,
        dealId: deal.id,
      });
      if (!canDelete) {
        toast({
          title: 'Cannot Delete',
          description: reason,
          status: 'warning',
          duration: 5000,
        });
        return;
      }
      setDealToDelete(deal);
      onOpen();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to check if deal can be deleted',
        status: 'error',
      });
    }
  };

  useEffect(() => {
    getDeals();
  }, [customerId]);

  const getDeals = async () => {
    setLoading(true);
    try {
      const _deals = await getDealsApi({ customerId });
      setDeals(_deals);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load deals',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!dealToDelete) return;
    try {
      await deleteDealApi({ customerId, dealId: dealToDelete.id });
      toast({
        title: 'Success',
        description: 'Deal deleted successfully',
        status: 'success',
      });
      onClose();
      setDealToDelete(null);
      getDeals();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete deal',
        status: 'error',
      });
    }
  };

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  return (
    <Box maxW={'container.lg'}>
      <Flex justifyContent={'end'}>
        <Button
          variant={'brand'}
          onClick={() => navigate(`/operations/${customerId}/deal/new`)}
        >
          Create Deal
        </Button>
      </Flex>
      <Card>
        <Heading
          variant={'sectionHeader'}
          borderColor={'twelve.green.300'}
          mb={10}
        >
          Deals
        </Heading>
        <Box mx={-5}>
          <DataTable
            data={deals}
            columns={columns}
            rowHeight={'100px'}
            onRowClick={(row) => {
              navigate(
                `/operations/${customerId}/deal/${row.id}`
              );
            }}
          />
        </Box>
      </Card>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Deal
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                Are you sure you want to delete "<strong>{dealToDelete?.name}</strong>"? This action cannot be undone.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteDeal}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

DealsOverview.propTypes = {};

export default DealsOverview;
